import { cn } from "@/lib/utils";

// Soft color palette indexed by colorIndex. Colors come from theme-aware tokens
// (--tone-* in themes/styles/*.js) so they stay legible in both light and dark themes.
const BADGE_COLORS = [
  { bg: "bg-tone-blue-soft", text: "text-tone-blue", border: "border-tone-blue/30" }, // 0
  { bg: "bg-tone-rose-soft", text: "text-tone-rose", border: "border-tone-rose/30" }, // 1
  { bg: "bg-tone-amber-soft", text: "text-tone-amber", border: "border-tone-amber/30" }, // 2
  { bg: "bg-tone-emerald-soft", text: "text-tone-emerald", border: "border-tone-emerald/30" }, // 3
  { bg: "bg-tone-violet-soft", text: "text-tone-violet", border: "border-tone-violet/30" }, // 4
  { bg: "bg-tone-orange-soft", text: "text-tone-orange", border: "border-tone-orange/30" }, // 5
  { bg: "bg-tone-rose-soft", text: "text-tone-rose", border: "border-tone-rose/30" }, // 6
  { bg: "bg-tone-purple-soft", text: "text-tone-purple", border: "border-tone-purple/30" }, // 7
  { bg: "bg-tone-slate-soft", text: "text-tone-slate", border: "border-tone-slate/30" }, // 8 (neutral)
];

/**
 * Badge Component
 *
 * @param {string} label - Text displayed inside the badge
 * @param {ReactNode} icon - Optional icon element displayed before the label
 * @param {string} variant - Style variant ("transparent" for neutral, otherwise solid colored)
 * @param {number} colorIndex - Index to pick color
 * @param {string} badgeStyle - styles for custom overrides
 * @param {string} className - Additional Tailwind / CSS classes
 * @param {string} title - Native tooltip. A badge is short by design, so the
 *   sentence behind the word goes here rather than beside it.
 */
export const Badge = ({ label, icon, variant = "transparent", badgeStyle = "", colorIndex = 0, className = "", title }) => {
  // Base Class
  const baseClass = cn("inline-flex items-center gap-1.5 text-sm select-none transition-all duration-200 rounded-md border", className);

  if (variant === "transparent") {
    return (
      <div className={cn(baseClass, "p-2 overflow-hidden bg-background-tertiary/30 border-border-primary text-text-secondary")} title={title}>
        <span>
          {icon && <span className="opacity-70 text-xs">{icon}</span>}
          {label}
        </span>
      </div>
    );
  }

  const color = BADGE_COLORS[colorIndex % BADGE_COLORS.length];

  return (
    <div className={cn(baseClass, "overflow-hidden", color.bg, color.border, color.text)} title={title}>
      <span className={cn("inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm w-full h-full", badgeStyle)}>
        {icon && <span className="opacity-80 text-xs">{icon}</span>}
        {/* A flex item, so `truncate` can shrink and ellipsize when a caller
            caps the badge's width — without this, a long label wrapped to a
            second line and spilled out of the pill. Where nothing constrains
            the width this span is inert and the badge keeps sizing to its
            label. */}
        <span className="min-w-0 truncate">{label}</span>
      </span>
    </div>
  );
};
